/**
 * @fileoverview Favorite-applications store for the Application Portal.
 *
 * A tiny localStorage-backed set of favorited app ids, exposed via
 * `useSyncExternalStore` so every consumer (the portal's Favorites filter and
 * each card's star toggle) stays in sync without prop-threading. Mirrors the
 * recent-apps store pattern in `hooks.ts`.
 */

import { useCallback, useSyncExternalStore } from "react";

import { api } from "./api";

const FAVORITE_APPS_KEY = "ic.shell.favoriteApps";
const RESOURCE_TYPE = "app";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(FAVORITE_APPS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

let favoritesMemo: string[] = readFavorites();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function persist(next: string[]): void {
  favoritesMemo = next;
  try {
    window.localStorage.setItem(FAVORITE_APPS_KEY, JSON.stringify(next));
  } catch {
    // ignore persistence failure — in-memory state still updates
  }
  emit();
}

/**
 * Replace the store with the server's favorite set and refresh the localStorage
 * cache. Called once on boot by favorites-sync after the auth token resolves;
 * the server is the source of truth from that point on.
 */
export function seedFromServer(ids: string[]): void {
  persist([...ids]);
}

/**
 * Optimistic toggle: update the UI + localStorage immediately, then sync the
 * server. On API failure, REVERT to the prior set so the UI never lies about
 * persisted state. Module-level so the closure always reads live state.
 */
function toggleFavoriteImpl(id: string): void {
  const prior = favoritesMemo;
  const next = prior.includes(id) ? prior.filter((x) => x !== id) : [...prior, id];
  persist(next);
  const request = next.includes(id)
    ? api.addFavorite(RESOURCE_TYPE, id)
    : api.removeFavorite(RESOURCE_TYPE, id);
  void request.catch(() => {
    persist(prior);
  });
}

export function useFavoriteApps(): {
  favorites: ReadonlySet<string>;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
} {
  const subscribe = (cb: () => void) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  };
  const ids = useSyncExternalStore(subscribe, () => favoritesMemo, () => favoritesMemo);
  const favorites = new Set(ids);

  const isFavorite = useCallback((id: string) => favoritesMemo.includes(id), []);
  const toggleFavorite = useCallback((id: string) => toggleFavoriteImpl(id), []);

  return { favorites, isFavorite, toggleFavorite };
}
